import type { Location } from "react-router";
import { dashboardReturnLocation } from "./navigation/deviceNavigation";
import { NavigationQueryProvider } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { useParams, useLocation, useNavigate } from "react-router";
import { useTenant } from "hooks/settings";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import DeviceInfo from "./DeviceInfo";
import SegmentsList from "./SegmentsList";
import { useListScrapEyeDevicesQuery } from "src/store/api/generatedApi";
import { TabSpec, TitledTabs } from "components/common/titledTabs";

import { NotFound } from "components/notFound";
import { VideoPlayerPanel } from "./VideoPlayer/VideoPlayerPanel";
import { LiveStreamPanel } from "./VideoPlayer/LiveStreamPanel";
import { RadioPanel } from "./Radio/RadioPanel";
import { LiveRadioPanel } from "./Radio/LiveRadioPanel";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link } from "react-router";

function ScrapEyeDeviceDataPageContent() {
  const { deviceName, tabKey } = useParams();
  const location: Location<unknown> = useLocation();
  const navigate = useNavigate();
  const tenantName = useTenant();
  const { data, isLoading } = useListScrapEyeDevicesQuery({
    tenantName,
    includeAdditionalInfo: false,
  });
  const device = data?.items.find((d) => d.name === deviceName);
  const { t } = useTenantTranslation();
  const id = device?.id ?? 0;
  // The site's SDR receiver lives on a single device, so the radio tabs only
  // appear where the backend reports a radio capability.
  const capabilities = device?.capabilities;
  const hasRadio = capabilities?.includes("radio") ?? false;
  const tabSpecs: TabSpec<string>[] = [
    {
      title: t("infoTab"),
      key: "info",
      content: <DeviceInfo deviceId={id} tenantName={tenantName} />,
    },
    {
      title: t("segmentsTitle"),
      key: "segments",
      content: <SegmentsList deviceId={id} tenantName={tenantName} />,
    },
    {
      title: t("videoPlayer"),
      key: "videoPlayer",
      content: (
        <VideoPlayerPanel deviceId={id} deviceName={device?.name ?? ""} />
      ),
    },
    {
      title: t("liveStream"),
      key: "liveStream",
      content: <LiveStreamPanel deviceId={id} />,
    },
    ...(hasRadio
      ? [
          {
            title: t("radio"),
            key: "radio",
            content: (
              <RadioPanel deviceId={id} deviceName={device?.name ?? ""} />
            ),
          },
          {
            title: t("liveRadio"),
            key: "liveRadio",
            content: <LiveRadioPanel deviceId={id} />,
          },
        ]
      : []),
  ];

  const selectedTabKey = tabKey ?? "info";
  const notFound = !tabSpecs.some((tab) => tab.key === selectedTabKey);
  const setSelectedTabKey = (key: string) =>
    navigate(
      {
        pathname: `/${tenantName}/admin/scrap-eye/data-dashboard/device/${encodeURIComponent(deviceName ?? "")}/${key}`,
        search: location.search,
        hash: location.hash,
      },
      { state: location.state }
    );
  // isLoading is checked before notFound: the radio tab keys are absent from
  // tabSpecs until the devices query resolves, so a radio deep-link would
  // otherwise flash NotFound on every load (and stick on a query error).
  if (isLoading) {
    return (
      <AccountPageLayout title={t("loading")}>
        <CircularProgress />
      </AccountPageLayout>
    );
  }
  if (!device) {
    return (
      <AccountPageLayout title={t("deviceNotFound")}>
        <Box>{t("deviceNotFound")}</Box>
      </AccountPageLayout>
    );
  }
  if (notFound) {
    return <NotFound />;
  }

  return (
    <EnsureGcsTokenExists>
      <Stack spacing={1}>
        <Stack pt={2} px={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Link
              to={dashboardReturnLocation({
                state: location.state,
                tenantName,
              })}
              style={{ textDecoration: "none" }}
            >
              <IconButton aria-label={t("back")}>
                <ArrowBackIcon />
              </IconButton>
            </Link>
            <Stack>
              <Typography variant="h1">{device.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("deviceLabelWithHash", { id: device.id })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <TitledTabs
          tabSpecs={tabSpecs}
          selectedTabKey={selectedTabKey}
          onChange={(key) => void setSelectedTabKey(key)}
        />
      </Stack>
    </EnsureGcsTokenExists>
  );
}

export default function ScrapEyeDeviceDataPage() {
  return (
    <NavigationQueryProvider>
      <ScrapEyeDeviceDataPageContent />
    </NavigationQueryProvider>
  );
}
